import { DownOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { Checkbox, Input } from 'antd5';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { None } from 'components/data/helpers/helpers';
import { Tag } from 'components/ui';

import {
  EmptyState,
  ExpandButton,
  LoadingState,
  MakeName,
  MakeRow,
  MakesList,
  ModelCountBadge,
  ModelRow,
  ModelsList,
  NestedFilterContainer,
  SearchRow,
  TagsRow,
} from './MakeModelNestedFilter.style';
import {
  type MakeModelOption,
  type MakeModelSelection,
  type SelectedMakeModel,
  expandMakeOnlySelections,
  filterMakesAndModelsBySearch,
  getMakeCheckState,
  getSelectedModelTags,
  isModelSelected,
  mergeModelPairsFromPayload,
  pairsFromFilterPayload,
  toFilterPayload,
  toggleMakeSelection,
  toggleModelSelection,
} from './utils';

type Props = {
  makes: MakeModelOption[];
  modelsByMake: Record<string, MakeModelOption[]>;
  selectedMakes: string[];
  selectedModels: string[];
  /** Prefer exact pairs when restoring (shared model names across Makes). */
  selectedPairs?: SelectedMakeModel[];
  onChange: (selection: { makes: string[]; models: string[]; pairs: SelectedMakeModel[] }) => void;
  disabled?: boolean;
  loading?: boolean;
  /** True when an Asset Type is selected (empty-state copy). */
  hasAssetTypeSelected?: boolean;
};

const displayOptionLabel = (option: MakeModelOption, noneFallback: ReactNode): ReactNode => {
  if (option.label !== undefined && option.label !== null && option.label !== '') {
    return option.label;
  }
  if (option.value === '') {
    return noneFallback;
  }
  return option.value;
};

const formatTagContent = (model: string, make: string, noneFallback: ReactNode): ReactNode => (
  <>
    {model === '' ? noneFallback : model}
    {' / '}
    {make === '' ? noneFallback : make}
  </>
);

export const MakeModelNestedFilter = ({
  makes,
  modelsByMake,
  selectedMakes,
  selectedModels,
  selectedPairs,
  onChange,
  disabled = false,
  loading = false,
  hasAssetTypeSelected = false,
}: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [expandedMakes, setExpandedMakes] = useState<Set<string>>(() => new Set());
  const [modelPairs, setModelPairs] = useState<SelectedMakeModel[]>(() =>
    selectedPairs?.length
      ? selectedPairs
      : pairsFromFilterPayload(selectedMakes, selectedModels, modelsByMake)
  );

  const noneLabel = <None />;
  const noneAccessibleLabel = t('common.none', 'none');

  const getAccessibleOptionLabel = (option: MakeModelOption): string => {
    if (typeof option.label === 'string' && option.label !== '') {
      return option.label;
    }
    if (option.value === '') {
      return noneAccessibleLabel;
    }
    return option.value;
  };

  useEffect(() => {
    if (selectedPairs?.length) {
      setModelPairs(selectedPairs);
      return;
    }
    setModelPairs(prev =>
      mergeModelPairsFromPayload(prev, selectedMakes, selectedModels, modelsByMake)
    );
  }, [selectedPairs, selectedMakes, selectedModels, modelsByMake]);

  const current: MakeModelSelection = useMemo(
    () =>
      expandMakeOnlySelections(
        {
          makes: selectedMakes,
          models: modelPairs,
        },
        modelsByMake
      ),
    [selectedMakes, modelPairs, modelsByMake]
  );

  const emitChange = (next: MakeModelSelection) => {
    setModelPairs(next.models);
    onChange(toFilterPayload(next, modelsByMake));
  };

  const filtered = useMemo(
    () => filterMakesAndModelsBySearch(makes, modelsByMake, search),
    [makes, modelsByMake, search]
  );

  const modelTags = useMemo(
    () => getSelectedModelTags(current, modelsByMake, makes),
    [current, modelsByMake, makes]
  );

  const makeOnlyTags = useMemo(
    () =>
      selectedMakes.filter(makeValue => {
        const models = modelsByMake[makeValue] ?? [];
        if (!models.length) {
          return true;
        }
        return !models.some(m => isModelSelected(makeValue, m.value, current));
      }),
    [selectedMakes, current, modelsByMake]
  );

  const handleToggleExpand = (makeValue: string) => {
    setExpandedMakes(prev => {
      const next = new Set(prev);
      if (next.has(makeValue)) {
        next.delete(makeValue);
      } else {
        next.add(makeValue);
      }
      return next;
    });
  };

  // Search-scoped Make toggle: only select models currently visible.
  const modelsByMakeForToggle = search.trim() ? filtered.modelsByMake : modelsByMake;

  const handleToggleMake = (makeValue: string) => {
    if (disabled) {
      return;
    }
    const next = toggleMakeSelection(makeValue, modelsByMakeForToggle, current);
    emitChange(next);
    setExpandedMakes(prev => new Set(prev).add(makeValue));
  };

  const handleToggleModel = (makeValue: string, modelValue: string) => {
    if (disabled) {
      return;
    }
    emitChange(toggleModelSelection(makeValue, modelValue, modelsByMake, current));
  };

  const handleRemoveMakeOnlyTag = (makeValue: string) => {
    if (disabled) {
      return;
    }
    emitChange(toggleMakeSelection(makeValue, modelsByMake, current));
  };

  return (
    <NestedFilterContainer data-testid="asset-make-model-nested-filter">
      {modelTags.length > 0 || makeOnlyTags.length > 0 ? (
        <TagsRow>
          {modelTags.map(tag => (
            <Tag
              key={`${tag.make}::${tag.model}`}
              closable={!disabled}
              onClose={e => {
                e.preventDefault();
                handleToggleModel(tag.make, tag.model);
              }}
            >
              {formatTagContent(tag.model, tag.make, noneLabel)}
            </Tag>
          ))}
          {makeOnlyTags.map(makeValue => {
            const makeOption = makes.find(m => m.value === makeValue) ?? {
              value: makeValue,
            };
            return (
              <Tag
                key={`make-only::${makeValue === '' ? '__none__' : makeValue}`}
                closable={!disabled}
                onClose={e => {
                  e.preventDefault();
                  handleRemoveMakeOnlyTag(makeValue);
                }}
              >
                {displayOptionLabel(makeOption, noneLabel)}
              </Tag>
            );
          })}
        </TagsRow>
      ) : null}

      <SearchRow>
        <Input
          allowClear
          disabled={disabled || loading}
          suffix={<SearchOutlined aria-hidden />}
          placeholder={t('table.selectMakeAndModels', 'Select make and models...')}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </SearchRow>

      <MakesList>
        {loading ? (
          <LoadingState>{t('table.loading', 'Loading...')}</LoadingState>
        ) : !filtered.makes.length ? (
          <EmptyState>
            {makes.length
              ? t('table.noMatchingMakesOrModels', 'No matching Makes or Models')
              : hasAssetTypeSelected
                ? t('table.noMakesForAssetType', 'No Makes found for this Asset Type')
                : t('table.selectAssetTypeForMakeModel', 'Select an Asset Type to filter by Make')}
          </EmptyState>
        ) : (
          filtered.makes.map(make => {
            const makeModels = filtered.modelsByMake[make.value] ?? [];
            const checkState = getMakeCheckState(make.value, modelsByMakeForToggle, current);
            const isExpanded = expandedMakes.has(make.value);
            const allMakeModels = modelsByMake[make.value] ?? [];
            const modelCount = allMakeModels.filter(model => model.value !== '').length;
            const hasModelOptions = allMakeModels.length > 0;
            const accessibleMakeLabel = getAccessibleOptionLabel(make);

            return (
              <div key={make.value === '' ? '__none_make__' : make.value}>
                <MakeRow
                  data-testid={`asset-make-row-${make.value === '' ? 'none' : make.value}`}
                  $checked={checkState !== 'unchecked'}
                  onClick={() => {
                    if (disabled || !hasModelOptions) {
                      return;
                    }
                    handleToggleExpand(make.value);
                  }}
                >
                  <Checkbox
                    aria-label={t('table.selectMake', 'Select {{make}}', {
                      make: accessibleMakeLabel,
                    })}
                    checked={checkState === 'checked'}
                    indeterminate={checkState === 'indeterminate'}
                    disabled={disabled}
                    onClick={e => e.stopPropagation()}
                    onChange={() => handleToggleMake(make.value)}
                  />
                  <MakeName>{displayOptionLabel(make, noneLabel)}</MakeName>
                  <ModelCountBadge>
                    {t('table.modelCount', '{{count}} models', { count: modelCount })}
                  </ModelCountBadge>
                  <ExpandButton
                    type="button"
                    aria-expanded={isExpanded}
                    aria-label={
                      isExpanded
                        ? t('table.collapseModels', 'Collapse {{make}} models', {
                            make: accessibleMakeLabel,
                          })
                        : t('table.expandModels', 'Expand {{make}} models', {
                            make: accessibleMakeLabel,
                          })
                    }
                    disabled={disabled || !hasModelOptions}
                    onClick={e => {
                      e.stopPropagation();
                      handleToggleExpand(make.value);
                    }}
                  >
                    {isExpanded ? <DownOutlined aria-hidden /> : <RightOutlined aria-hidden />}
                  </ExpandButton>
                </MakeRow>

                {isExpanded && makeModels.length > 0 ? (
                  <ModelsList>
                    {makeModels.map(model => {
                      const isChecked = isModelSelected(make.value, model.value, current);
                      const accessibleModelLabel = getAccessibleOptionLabel(model);
                      return (
                        <ModelRow
                          key={`${make.value}::${model.value === '' ? '__none__' : model.value}`}
                          data-testid={`asset-model-row-${make.value === '' ? 'none' : make.value}-${model.value === '' ? 'none' : model.value}`}
                          $checked={isChecked}
                          onClick={() => {
                            if (disabled) {
                              return;
                            }
                            handleToggleModel(make.value, model.value);
                          }}
                        >
                          <Checkbox
                            aria-label={t('table.selectMakeModel', 'Select {{model}} / {{make}}', {
                              model: accessibleModelLabel,
                              make: accessibleMakeLabel,
                            })}
                            checked={isChecked}
                            disabled={disabled}
                            onClick={e => e.stopPropagation()}
                            onChange={() => handleToggleModel(make.value, model.value)}
                          />
                          <span>{displayOptionLabel(model, noneLabel)}</span>
                        </ModelRow>
                      );
                    })}
                  </ModelsList>
                ) : null}
              </div>
            );
          })
        )}
      </MakesList>
    </NestedFilterContainer>
  );
};
